import React, { useState } from "react";
import { FaChevronDown, FaChevronUp, FaChevronRight, FaTimes } from "react-icons/fa";

const Section = ({ title, open, onToggle, children }) => (
	<div className="border-b border-gray-200 last:border-b-0 py-3">
		<button
			type="button"
			onClick={onToggle}
			className="w-full flex justify-between items-center text-left"
			aria-expanded={open}
		>
			<h3 className="text-sm font-semibold text-gray-900">{title}</h3>
			{open ? (
				<FaChevronUp size={12} className="text-gray-500" />
			) : (
				<FaChevronDown size={12} className="text-gray-500" />
			)}
		</button>
		{open && <div className="mt-3 space-y-2">{children}</div>}
	</div>
);


const ProductsSidebar = ({
	onClose,
	filters,
	onFilter,
	onReset,
	subcategories = [],
	onSubcategorySelect,
	selectedSubcategory,
}) => {
	const [isBrandOpen, setBrandOpen] = useState(true);
	const [isPriceOpen, setPriceOpen] = useState(true);
	const [isRatingOpen, setRatingOpen] = useState(true);
	const [isSubcategoryOpen, setSubcategoryOpen] = useState(true);
	const [openSubcategories, setOpenSubcategories] = useState({});
	const [selected, setSelected] = useState({ brands: [], price: null, rating: null });

	const toggleSubcategory = (id) =>
		setOpenSubcategories((prev) => ({ ...prev, [id]: !prev[id] }));

	const applyFilters = () => {
		onFilter(selected);
		if (onClose) onClose();
	};

	const resetFilters = () => {
		setSelected({ brands: [], price: null, rating: null });
		onReset();
		if (onClose) onClose();
	};

	// inline padding instead of dynamic `ml-${n}` classes (Tailwind can't see those)
	const renderSubcategories = (items, level = 0) =>
		items.map((subcat) => (
			<div key={subcat.id}>
				<div className="flex items-center" style={{ paddingLeft: level * 14 }}>
					{subcat.subcategories?.length > 0 ? (
						<button
							type="button"
							onClick={() => toggleSubcategory(subcat.id)}
							className="mr-1 w-4 text-gray-500 hover:text-yellow-800"
							aria-label={`Toggle ${subcat.name}`}
						>
							{openSubcategories[subcat.id] ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
						</button>
					) : (
						<span className="w-4 mr-1" />
					)}
					<label className="flex items-center flex-grow cursor-pointer hover:bg-yellow-50 px-2 py-1 rounded">
						<input
							type="radio"
							name="subcategory"
							checked={selectedSubcategory === subcat.id}
							onChange={() => onSubcategorySelect(subcat.id)}
							className="mr-2 h-4 w-4 accent-yellow-800"
						/>
						<span
							className={`text-sm ${selectedSubcategory === subcat.id ? "text-yellow-800 font-medium" : "text-gray-700"
								}`}
						>
							{subcat.name}
						</span>
					</label>
				</div>

				{subcat.subcategories?.length > 0 && openSubcategories[subcat.id] && (
					<div>{renderSubcategories(subcat.subcategories, level + 1)}</div>
				)}
			</div>
		));

	const optionLabel = "flex items-center gap-2 cursor-pointer text-sm text-gray-700";

	return (
		<div className="bg-white md:border md:border-gray-200 md:rounded-lg flex flex-col min-h-full">
			{/* Header */}
			<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
				<h2 className="text-base font-semibold text-gray-900">Filters</h2>
				<button
					type="button"
					className="md:hidden text-gray-500 hover:text-gray-800"
					onClick={onClose}
					aria-label="Close filters"
				>
					<FaTimes />
				</button>
			</div>

			<div className="px-4 flex-1">
				{subcategories.length > 0 && (
					<Section
						title="Subcategories"
						open={isSubcategoryOpen}
						onToggle={() => setSubcategoryOpen((o) => !o)}
					>
						<div className="space-y-0.5 -ml-1">{renderSubcategories(subcategories)}</div>
					</Section>
				)}

				{filters.brands?.length > 0 && (
					<Section title="Brands" open={isBrandOpen} onToggle={() => setBrandOpen((o) => !o)}>
						{filters.brands.map((brand) => (
							<label key={brand} className={optionLabel}>
								<input
									type="checkbox"
									checked={selected.brands.includes(brand)}
									onChange={() =>
										setSelected((prev) => ({
											...prev,
											brands: prev.brands.includes(brand)
												? prev.brands.filter((b) => b !== brand)
												: [...prev.brands, brand],
										}))
									}
									className="h-4 w-4 rounded accent-yellow-800"
								/>
								{brand}
							</label>
						))}
					</Section>
				)}

				{filters.priceRanges?.length > 0 && (
					<Section title="Price" open={isPriceOpen} onToggle={() => setPriceOpen((o) => !o)}>
						{filters.priceRanges.map((range, idx) => (
							<label key={idx} className={optionLabel}>
								<input
									type="radio"
									name="price"
									checked={selected.price?.label === range.label}
									onChange={() => setSelected((prev) => ({ ...prev, price: range }))}
									className="h-4 w-4 accent-yellow-800"
								/>
								{range.label}
							</label>
						))}
					</Section>
				)}

				{filters.ratings?.length > 0 && (
					<Section title="Ratings" open={isRatingOpen} onToggle={() => setRatingOpen((o) => !o)}>
						{filters.ratings.map((rate) => (
							<label key={rate} className={optionLabel}>
								<input
									type="radio"
									name="rating"
									checked={selected.rating === rate}
									onChange={() => setSelected((prev) => ({ ...prev, rating: rate }))}
									className="h-4 w-4 accent-yellow-800"
								/>
								{rate}★ &amp; above
							</label>
						))}
					</Section>
				)}
			</div>

			{/* Actions */}
			<div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 grid grid-cols-2 gap-3">
				<button
					type="button"
					onClick={resetFilters}
					className="py-2 rounded-md border border-yellow-800 text-yellow-800 text-sm font-semibold hover:bg-yellow-50"
				>
					Reset
				</button>
				<button
					type="button"
					onClick={applyFilters}
					className="py-2 rounded-md bg-yellow-800 text-white text-sm font-semibold hover:bg-yellow-900"
				>
					Apply
				</button>
			</div>
		</div>
	);
};

export default ProductsSidebar;